import React, { useEffect, useState, useCallback } from 'react';
import {Modal, Button, Space, message, Switch, Tooltip, Alert, Divider} from 'antd';
import { InfoCircleOutlined, CopyOutlined } from '@ant-design/icons';
import QRCode from 'react-qr-code';
import { copyToClipboard } from '../../helpers/clipboard';
import ShortUrlStatsModal from '../ShortUrlStatsModal';
import Cart from '../Header/Cart';
import { useTranslation } from '../../hooks/useTranslation';

interface ShortUrlModalProps {
  /** 是否显示弹框 */
  open: boolean;
  /** 关闭弹框的回调 */
  onCancel: () => void;
  /** 要生成短网址的原始URL */
  url: string;
  /** 链接类型 */
  mode?: 'short' | 'mufy';
}

/**
 * 短网址弹框组件
 * 
 * @author dongdong.fdd
 * @date 2025/01/XX
 */
const ShortUrlModal: React.FC<ShortUrlModalProps> = ({
  open,
  onCancel,
  url,
  mode = 'short',
}) => {
  const { t } = useTranslation();
  const [shortUrl, setShortUrl] = useState<string>('');
  const [shortId, setShortId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [isVip, setIsVip] = useState(false);
  const [enableStats, setEnableStats] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsModalOpen, setStatsModalOpen] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const isMufyMode = mode === 'mufy';

  // 创建短网址
  const createShortUrl = useCallback(async () => {
    if (!url) {
      message.error(t('shortUrl.urlRequired'));
      return;
    }

    setLoading(true);
    try {
      const endpoint = isMufyMode ? '/u/mufy/save' : '/u/save';
      const response = await fetch(`${endpoint}?url=${encodeURIComponent(url)}`, {
        method: 'POST',
      });
      const json = await response.json();
      
      if (json && json.shortId) {
        const generatedShortUrl = isMufyMode
          ? (json.fullMufyUrl || `https://u.fukit.cn/mufy/${json.shortId}`)
          : (json.fullShortUrl || `https://z.wiki/u/${json.shortId}`);
        setShortUrl(generatedShortUrl);
        setShortId(json.shortId);
        // 获取统计状态（如果后端返回了enableStats字段）
        if (json.enableStats !== undefined) {
          setEnableStats(json.enableStats);
        } else {
          // 默认关闭
          setEnableStats(false);
        }
      } else {
        message.error(t('shortUrl.createFailed'));
      }
    } catch (error) {
      console.error('创建短网址失败:', error);
      message.error(t('shortUrl.createFailed'));
    } finally {
      setLoading(false);
    }
  }, [url, t, isMufyMode]);

  // 获取用户VIP状态
  useEffect(() => {
    fetch('/login/info')
      .then(res => res.json())
      .then(data => {
        if (data.isSuccess && data.detail) {
          setIsVip(data.detail.isActiveVip || false);
        }
      })
      .catch(err => {
        console.error('获取用户信息失败:', err);
      });
  }, []);

  // 当弹框打开时，自动创建短网址
  useEffect(() => {
    if (open && url) {
      setShortUrl('');
      setShortId('');
      setEnableStats(false);
      createShortUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, url]);

  // 处理复制短网址
  const handleCopy = () => {
    if (shortUrl) {
      copyToClipboard(shortUrl, isMufyMode ? t('mufyLink.linkCopied') : t('shortUrl.linkCopied'));
      onCancel();
    }
  };

  // 处理开启/关闭统计
  const handleToggleStats = async (checked: boolean) => {
    if (!isVip) {
      Modal.warning({
        title: t('shortUrl.vipOnly'),
        content: t('shortUrl.vipOnlyMessage'),
      });
      return;
    }

    if (!shortId) {
      message.error(t('shortUrl.idNotFound'));
      return;
    }

    setStatsLoading(true);
    try {
      const endpoint = checked ? '/u/enableStats' : '/u/disableStats';
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: `shortId=${encodeURIComponent(shortId)}`,
      });
      const json = await response.json();
      
      if (json.success) {
        setEnableStats(checked);
        message.success(json.message || (checked ? t('shortUrl.statsEnabled') : t('shortUrl.statsDisabled')));
      } else {
        message.error(json.message || t('shortUrl.operationFailed'));
      }
    } catch (error) {
      console.error('操作失败:', error);
      message.error(t('shortUrl.operationFailedRetry'));
    } finally {
      setStatsLoading(false);
    }
  };

  // 处理查看统计
  const handleViewStats = () => {
    if (!isVip) {
      Modal.warning({
        title: t('shortUrl.vipOnly'),
        content: t('shortUrl.vipOnlyMessage'),
      });
      return;
    }

    if (!enableStats) {
      message.warning(t('shortUrl.enableStatsFirst'));
      return;
    }

    setStatsModalOpen(true);
  };

  return (
    <Modal
      title={isMufyMode ? t('mufyLink.title') : t('shortUrl.title')}
      open={open}
      onCancel={onCancel}
      footer={
        <Space>
          <Button type="default" onClick={onCancel}>
            {t('common.close')}
          </Button>
        </Space>
      }
      width={500}
    >
      <div
        className="qr-code-ctn"
        style={{
          marginRight: '34px',
        }}
      >
        {loading ? (
          <p>{isMufyMode ? t('mufyLink.generating') : t('shortUrl.generating')}</p>
        ) : shortUrl ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <p className="url" style={{ margin: 0, flex: 1, wordBreak: 'break-all' }}>
                <a href={shortUrl} target="_blank" rel="noopener noreferrer">
                  {shortUrl}
                </a>
              </p>
              <Tooltip title={isMufyMode ? t('mufyLink.copyMufyUrl') : t('shortUrl.copyShortUrl')}>
                <Button
                  type="primary"
                  onClick={handleCopy}
                  disabled={!shortUrl || loading}
                  loading={loading}
                >
                  {t('shortUrl.copy')}
                </Button>
              </Tooltip>
            </div>
            {isMufyMode && (
              <Alert
                message={t('mufyLink.loadFailedContact')}
                type="warning"
                showIcon
                style={{ margin: '12px 0' }}
              />
            )}
            <div className="qr-code" style={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}>
              <QRCode
                size={128}
                style={{ height: "auto", maxWidth: "40%", width: "40%" }}
                value={shortUrl}
                viewBox={`0 0 128 128`}
              />
            </div>

            <Divider>{t('shortUrl.additionalFeatures')}</Divider>
            {/* 统计功能区域 */}
            <div style={{}}>
              {/* 统计功能说明提示 */}
              {!enableStats && (
                <Alert
                  message={
                    <div>
                      <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '14px' }}>
                        <InfoCircleOutlined style={{ marginRight: '4px', color: '#1890ff' }} />
                        {t('shortUrl.statsDescriptionTitle')}
                      </div>
                      <div style={{ fontSize: '13px', lineHeight: '1.8', color: '#666' }}>
                        <div>• <strong>{t('shortUrl.statsTotalPv')}</strong> - {t('shortUrl.statsTotalPvDesc')}</div>
                        <div>• <strong>{t('shortUrl.statsUniqueIp')}</strong> - {t('shortUrl.statsUniqueIpDesc')}</div>
                        <div>• <strong>{t('shortUrl.statsTodayPv')}</strong> - {t('shortUrl.statsTodayPvDesc')}</div>
                        <div>• <strong>{t('shortUrl.statsProvince')}</strong> - {t('shortUrl.statsProvinceDesc')}</div>
                        <div>• <strong>{t('shortUrl.statsBrowser')}</strong> - {t('shortUrl.statsBrowserDesc')}</div>
                        <div>• <strong>{t('shortUrl.statsOs')}</strong> - {t('shortUrl.statsOsDesc')}</div>
                        <div>• <strong>{t('shortUrl.statsDevice')}</strong> - {t('shortUrl.statsDeviceDesc')}</div>
                        <div>• <strong>{t('shortUrl.statsRecent')}</strong> - {t('shortUrl.statsRecentDesc')}</div>
                      </div>
                    </div>
                  }
                  type="info"
                  showIcon={false}
                  style={{ 
                    marginBottom: '16px',
                    backgroundColor: '#e6f7ff',
                    border: '1px solid #91d5ff',
                  }}
                />
              )}

              <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Tooltip title={!isVip ? t('shortUrl.vipOnlyTooltip') : ''}>
                  <Switch
                    checked={enableStats}
                    onChange={handleToggleStats}
                    disabled={statsLoading}
                    loading={statsLoading}
                  />
                </Tooltip>
                <span style={{ fontSize: '14px', fontWeight: '500' }}>
                  {t('shortUrl.enableStats')}
                  {!isVip && <span style={{ color: '#ff4d4f', marginLeft: '4px' }}>(VIP)</span>}
                </span>
              </div>
              
              {enableStats && (
                <Alert
                  message={t('shortUrl.statsEnabledMessage')}
                  type="success"
                  showIcon
                  style={{ marginBottom: '12px' }}
                />
              )}

              <Space direction={'horizontal'} align={'start'}>
                <Button
                  type={enableStats ? "primary" : "default"}
                  onClick={handleViewStats}
                  disabled={!enableStats || !isVip}
                  style={{ width: '100%' }}
                >
                  {t('shortUrl.viewStats')}
                </Button>

                {/* 非VIP用户显示购买会员按钮 */}
                {!isVip && (
                  <Button
                    type={'dashed'}
                    onClick={() => setShowCart(true)}
                    style={{ width: '100%', marginBottom: '12px' }}
                  >
                    {t('shortUrl.vipOnlyBuy')}
                  </Button>
                )}
              </Space>
            </div>
          </>
        ) : (
          <p>{t('shortUrl.generateFailed')}</p>
        )}
      </div>
      
      {/* 统计数据弹框 */}
      {shortId && (
        <ShortUrlStatsModal
          open={statsModalOpen}
          onCancel={() => setStatsModalOpen(false)}
          shortId={shortId}
        />
      )}
      
      {/* VIP 中心弹框 */}
      <Modal 
        title={t('shortUrl.vipCenter')} 
        footer={null} 
        width="80%" 
        open={showCart} 
        okText={t('common.close')} 
        cancelText={t('common.close')}
        onCancel={() => setShowCart(false)} 
        onOk={() => setShowCart(false)}
      >
        <Cart />
      </Modal>
    </Modal>
  );
};

export default ShortUrlModal;
